import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {BehaviorSubject, catchError, Observable, throwError} from 'rxjs';
import {map, tap} from 'rxjs/operators';
import {Client, ClientPost} from './client.interface';
import {environment} from '@/environments/environment';

interface PaginatedResponse<T> {
  content: T[];
  totalElements: number;
  number: number;
  size: number;
  pageable?: {
    pageNumber: number;
    pageSize: number;
  };
}

@Injectable({providedIn: 'root'})
export class ClientsService {
  public http = inject(HttpClient);
  private apiUrl = `${environment.apiUrl}/clients`;

  private clientsSubject = new BehaviorSubject<Client[]>([]);
  private clientsLoaded = false;

  private allClientsSubject = new BehaviorSubject<Client[]>([]);
  private allClientsLoaded = false;

  getClients(): Observable<Client[]> {
    if (!this.clientsLoaded) {
      this.refreshClients();
    }
    return this.clientsSubject.asObservable();
  }

  getClientsPaginated(page: number = 0, size: number = 10, name?: string): Observable<PaginatedResponse<Client>> {
    const params: Record<string, string> = { page: page.toString(), size: size.toString() };
    if (name?.trim()) {
      params['name'] = name.trim();
    }
    return this.http.get<PaginatedResponse<Client>>(this.apiUrl, { params })
      .pipe(catchError(this.handleError));
  }

  getAllClients(): Observable<Client[]> {
    if (!this.allClientsLoaded) {
      this.loadAllClients();
    }
    return this.allClientsSubject.asObservable();
  }

  refreshClients(): void {
    this.clientsLoaded = true;
    this.http.get<PaginatedResponse<Client>>(this.apiUrl)
      .pipe(catchError(this.handleError))
      .subscribe({
        next: (response) => {
          const clients = response.content || [];
          const sortedClients = clients.sort((a, b) => a.name.localeCompare(b.name));
          this.clientsSubject.next(sortedClients);
        },
        error: () => this.clientsLoaded = false
      });
  }

  private loadAllClients(): void {
    this.allClientsLoaded = true;
    const params = { page: '0', size: '1000' };
    this.http.get<PaginatedResponse<Client>>(this.apiUrl, { params })
      .pipe(catchError(this.handleError))
      .subscribe({
        next: (response) => {
          const clients = response.content || [];
          const sortedClients = clients.sort((a, b) => a.name.localeCompare(b.name));
          this.allClientsSubject.next(sortedClients);
        },
        error: () => this.allClientsLoaded = false
      });
  }

  refreshAllClients(): void {
    this.allClientsLoaded = false;
    this.loadAllClients();
  }

  getClient(id: string): Observable<Client> {
    return this.http.get<Client>(`${this.apiUrl}/${id}`)
      .pipe(catchError(this.handleError));
  }

  createClient(client: ClientPost): Observable<ClientPost> {
    return this.http.post<ClientPost>(this.apiUrl, client)
      .pipe(
        catchError(this.handleError),
        tap(() => this.refreshAllClients())
      );
  }

  updateClient(id: string, client: ClientPost): Observable<Client> {
    return this.http.put<Client>(`${this.apiUrl}/${id}`, client)
      .pipe(
        catchError(this.handleError),
        tap(() => this.refreshAllClients())
      );
  }

  deleteClient(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`)
      .pipe(
        catchError(this.handleError),
        tap(() => this.refreshAllClients())
      );
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMsg = 'Ocurrió un error inesperado';
    if (error.error && error.error.message) {
      errorMsg = error.error.message; // <- aquí capturas el mensaje real
    }
    return throwError(() => new Error(errorMsg));
  }
}
